import http, { API_BASE } from './http'
import type {
  Batch,
  ConvertPayload,
  FileCategory,
  FileSettings,
  IdsPayload,
  Job,
  MaterialOverrides,
  UserFile,
} from '@/types/api'

export const filesService = {
  async list(category?: FileCategory): Promise<UserFile[]> {
    const { data } = await http.get<UserFile[]>('/files', { params: { category } })
    return data
  },

  async upload(file: File): Promise<UserFile> {
    const form = new FormData()
    form.append('file', file)
    const { data } = await http.post<UserFile>('/files/upload', form, {
      headers: { 'Content-Type': undefined },
    })
    return data
  },

  async uploadWatermark(file: File): Promise<UserFile> {
    const form = new FormData()
    form.append('file', file)
    const { data } = await http.post<UserFile>('/files/watermark', form, {
      headers: { 'Content-Type': undefined },
    })
    return data
  },

  async convert(ids: string[], format: string): Promise<Batch<Job>> {
    const payload: ConvertPayload = { ids, format }
    const { data } = await http.post<Batch<Job>>('/files/convert', payload)
    return data
  },

  async saveSettings(id: string, settings: FileSettings): Promise<UserFile> {
    const { data } = await http.post<UserFile>(`/files/${id}/settings`, settings)
    return data
  },

  async saveMaterials(id: string, overrides: MaterialOverrides): Promise<UserFile> {
    const { data } = await http.post<UserFile>(`/files/${id}/materials`, overrides)
    return data
  },

  async raw(id: string, signal?: AbortSignal): Promise<string> {
    const { data } = await http.get<string>(`/files/${id}/raw`, {
      responseType: 'text',
      transformResponse: [(body: string) => body],
      headers: { 'Content-Type': undefined },
      withCredentials: false,
      signal,
    })
    return data
  },

  rawUrl(id: string): string {
    return `${API_BASE}/files/${id}/raw`
  },

  previewUrl(id: string | null): string | undefined {
    return id ? this.rawUrl(id) : undefined
  },

  thumbnailUrl(id: string): string {
    return `${API_BASE}/files/${id}/thumbnail`
  },

  downloadUrl(ids: string[]): string {
    return `${API_BASE}/files/download?ids=${ids.join(',')}`
  },

  examplesUrl(): string {
    return `${API_BASE}/files/examples`
  },

  async remove(ids: string[]): Promise<void> {
    const payload: IdsPayload = { ids }
    await http.delete('/files', { data: payload })
  },
}
